import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';

import { authAPI } from '../../services/api';

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
    const verifiedTokenRef = useRef('');

    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
            setIsSuccess(false);
            setMessage('Verification token is missing. Please use the link from your email.');
            setIsLoading(false);
            return;
        }

        // React StrictMode runs effects twice in development; ensure we verify once per token.
        if (verifiedTokenRef.current === token) {
            return;
        }
        verifiedTokenRef.current = token;

        const verify = async () => {
            try {
                const response = await authAPI.verifyEmail(token);
                setIsSuccess(true);
                setMessage(response.message || 'Email verified successfully. You can now log in.');
            } catch (error) {
                setIsSuccess(false);
                setMessage(error.message || 'Email verification failed. Please request a new verification email.');
            } finally {
                setIsLoading(false);
            }
        };

        verify();
    }, [token]);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                p: 2,
            }}
        >
            <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 480, borderRadius: 2 }}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                    Email Verification
                </Typography>

                {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={22} />
                        <Typography>Verifying your email...</Typography>
                    </Box>
                ) : (
                    <Alert severity={isSuccess ? 'success' : 'error'} sx={{ mb: 3 }}>
                        {message}
                    </Alert>
                )}

                <Button component={Link} to="/login" variant="contained" fullWidth>
                    Go to Login
                </Button>
            </Paper>
        </Box>
    );
};

export default VerifyEmail;
